import { HttpError } from "../HttpClient";
import errorCodes from "./errorCodes.json";

/**
 * Extracts a user-friendly error message from an HttpError or generic error with response data.
 *
 * @param error - The error object (can be HttpError, error with response.data, or unknown)
 * @param defaultMessage - Optional default message to use if no errorCode is found
 * @returns A user-friendly error message in Portuguese
 *
 * @example
 * ```typescript
 * try {
 *   await someService.someMethod();
 * } catch (error) {
 *   const message = getErrorMessage(error, "Não foi possível executar a operação. Tente novamente.");
 *   throw new Error(message);
 * }
 * ```
 */
export function getErrorMessage(
  error: unknown,
  defaultMessage: string = "Não foi possível concluir a operação. Tente novamente. Se o problema continuar, contacte a equipa de Suporte.",
): string {
  let responseData: any = null;

  if (error instanceof HttpError && error.responseData) {
    responseData = error.responseData;
  } else if (
    error &&
    typeof error === "object" &&
    "response" in error &&
    error.response &&
    typeof error.response === "object" &&
    "data" in error.response
  ) {
    responseData = (error as { response: { data: any } }).response.data;
  }

  if (responseData) {
    const errorCode = responseData.errorCode;

    // Handle ERROR_VALIDATION_TRANSLATED special case
    if (errorCode === "ERROR_VALIDATION_TRANSLATED") {
      if (responseData.details && Array.isArray(responseData.details)) {
        const detailsArray = responseData.details.filter(
          (detail: any) => typeof detail === "string",
        );
        if (detailsArray.length > 0) {
          return detailsArray.join("\n");
        }
      }
      // Fallback to default message if details is not valid
      return defaultMessage;
    }

    // Handle HEALTHBOX_INVALID_SKU_OR_NOT_IN_STOCK special case
    if (errorCode === "HEALTHBOX_INVALID_SKU_OR_NOT_IN_STOCK") {
      const baseMessage =
        (errorCodes as Record<string, string>)[errorCode] ||
        "SKU para Healthbox inválido ou sem stock";

      if (responseData.details && Array.isArray(responseData.details)) {
        const skus = responseData.details.filter(
          (detail: any) => typeof detail === "string",
        );
        if (skus.length > 0) {
          return `${baseMessage}: ${skus.join(", ")}`;
        }
      }

      return baseMessage;
    }

    if (errorCode && typeof errorCode === "string" && errorCode in errorCodes) {
      return (errorCodes as Record<string, string>)[errorCode];
    }
  }

  return defaultMessage;
}
